import { EMRdetailsProps, IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import Input from "@/app/components/ui/Inputs/Input";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { IEMRClinicConfig } from "@/app/hooks/api_hooks/emr/useEMRClinicConfigAPI";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";

interface VitalsFormProps {
  heart_rate: string;
  respiratory_rate: string;
  mucous_membrane: string;
  hydration: string;
  pain: string;
}

interface VitalsComponentProps extends EMRdetailsProps {
  clinicConfig: IEMRClinicConfig | null;
}

const VitalsComponent = ({ emrDetails, setEmrDetails, clinicConfig }: VitalsComponentProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const isFirstLoad = useRef(true);

  const formHook = useForm<VitalsFormProps>({ mode: "onChange" });

  const debouncedUpdate = useMemo(
    () =>
      debounce((vitals: Partial<VitalsFormProps>) => {
        const constructedBody: IEMRUpdatePayloadProps = {
          vitals,
          emr_status: emrDetails?.emr_status,
        };
        updateEmrAPI(emr_id, constructedBody).then(({ status, message }) => {
          if (status) setEmrDetails(message);
        });
      }, 500),
    [emr_id, emrDetails?.emr_status, updateEmrAPI],
  );

  useEffect(() => {
    if (emrDetails?.vitals) {
      isFirstLoad.current = true;
      const v = emrDetails.vitals;
      formHook.reset({
        heart_rate: v.heart_rate || "",
        respiratory_rate: v.respiratory_rate || "",
        mucous_membrane: v.mucous_membrane || "",
        hydration: v.hydration || "",
        pain: v.pain || "",
      });
      setTimeout(() => { isFirstLoad.current = false; }, 0);
    }
  }, [emrDetails]);

  const handleNumericChange = useCallback(
    (field: "heart_rate" | "respiratory_rate") => {
      const values = formHook.getValues();
      debouncedUpdate({ ...values, [field]: formHook.getValues(field) });
    },
    [formHook, debouncedUpdate],
  );

  const handleSelectChange = useCallback(
    (field: "mucous_membrane" | "hydration" | "pain", value: string) => {
      formHook.setValue(field, value);
      const values = formHook.getValues();
      debouncedUpdate({ ...values, [field]: value });
    },
    [formHook, debouncedUpdate],
  );

  useEffect(() => () => { debouncedUpdate.cancel(); }, [debouncedUpdate]);

  if (!clinicConfig?.vitals_enabled) return null;

  const mmOptions = clinicConfig.mucous_membrane_options || [];
  const hydrationOptions = clinicConfig.hydration_options || [];
  const painOptions = clinicConfig.pain_options || [];

  return (
    <div className="w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Vitals</h3>
      <div className="bg-[#F9FAFB] w-full rounded-xl p-2 flex flex-wrap gap-4 items-start mt-2">
        {/* Heart Rate */}
        <div className="w-full sm:w-[180px]">
          <p className="text-xs mb-1">Heart Rate</p>
          <Input
            endIcon={<span className="text-gray-500 text-xs">bpm</span>}
            className="text-[#000000] rounded-md w-full sm:w-[180px] h-9 text-sm"
            inputClassName="text-xs h-9"
            placeholder="Heart Rate"
            register={formHook.register("heart_rate", {
              pattern: { value: /^\d+$/, message: "Whole numbers only" },
              onChange: () => handleNumericChange("heart_rate"),
            })}
            fieldError={formHook.formState.errors.heart_rate}
            errorMessages={[{ type: "pattern", message: "Whole numbers only" }]}
          />
        </div>

        {/* Respiratory Rate */}
        <div className="w-full sm:w-[180px]">
          <p className="text-xs mb-1">Respiratory Rate</p>
          <Input
            endIcon={<span className="text-gray-500 text-xs">rpm</span>}
            className="text-[#000000] rounded-md w-full sm:w-[180px] h-9 text-sm"
            inputClassName="text-xs h-9"
            placeholder="Respiratory Rate"
            register={formHook.register("respiratory_rate", {
              pattern: { value: /^\d+$/, message: "Whole numbers only" },
              onChange: () => handleNumericChange("respiratory_rate"),
            })}
            fieldError={formHook.formState.errors.respiratory_rate}
            errorMessages={[{ type: "pattern", message: "Whole numbers only" }]}
          />
        </div>

        {/* Mucous Membrane - Multi Option */}
        <div className="w-full sm:w-[220px]">
          <p className="text-xs mb-1">Mucous Membrane</p>
          <div className="flex flex-wrap gap-1">
            {mmOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelectChange("mucous_membrane", opt)}
                className={`px-2 py-1 rounded-full text-xs border ${
                  formHook.watch("mucous_membrane") === opt
                    ? "bg-[#0B6EFF] text-white border-[#0B6EFF]"
                    : "bg-white text-[#6C727F] border-[#E2E2E2]"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Hydration */}
        <div className="w-full sm:w-[180px]">
          <p className="text-xs mb-1">Hydration (CRT Sec)</p>
          <div className="flex flex-wrap gap-1">
            {hydrationOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelectChange("hydration", opt)}
                className={`px-3 py-1 rounded-full text-xs border ${
                  formHook.watch("hydration") === opt
                    ? "bg-[#0B6EFF] text-white border-[#0B6EFF]"
                    : "bg-white text-[#6C727F] border-[#E2E2E2]"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Pain */}
        <div className="w-full sm:w-[180px]">
          <p className="text-xs mb-1">Pain</p>
          <div className="flex flex-wrap gap-1">
            {painOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelectChange("pain", opt)}
                className={`px-3 py-1 rounded-full text-xs border ${
                  formHook.watch("pain") === opt
                    ? "bg-[#0B6EFF] text-white border-[#0B6EFF]"
                    : "bg-white text-[#6C727F] border-[#E2E2E2]"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VitalsComponent;
